import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { ProcessingStatus as Status, ContentDocument } from '@shared/types/content';
import { LoadingFacts } from '../ui/LoadingFacts';

interface ProcessingStatusProps {
  status: Status;
  document: ContentDocument | null;
}

const stages: Array<{ key: string; label: string; estimate: string; weight: number }> = [
  { key: 'extracting', label: 'Extracting text from your document...', estimate: '~5 seconds', weight: 5 },
  { key: 'classifying', label: 'Identifying document type...', estimate: '~5 seconds', weight: 10 },
  { key: 'parsing', label: 'Identifying structure and concepts...', estimate: '30–90 seconds', weight: 70 },
  { key: 'assessing', label: 'Ready for your input', estimate: '', weight: 85 },
  { key: 'embedding', label: 'Preparing your learning experience...', estimate: '~15 seconds', weight: 95 },
  { key: 'complete', label: 'Your learning path is ready', estimate: '', weight: 100 },
];

function getStageProgress(status: string): number {
  const stage = stages.find((s) => s.key === status);
  return stage?.weight ?? 0;
}

function getStageEstimate(status: string): string {
  const stage = stages.find((s) => s.key === status);
  return stage?.estimate ?? '';
}

export function ProcessingStatusDisplay({ status, document: doc }: ProcessingStatusProps) {
  const stageInfo = stages.find((s) => s.key === status);
  const label = stageInfo?.label ?? 'Processing...';
  const isProcessing = !['complete', 'error', 'assessing'].includes(status);
  const conceptCount = doc?.concepts?.length ?? 0;
  const estimate = getStageEstimate(status);

  // Animated progress that smoothly fills between stage milestones
  const targetProgress = getStageProgress(status);
  const [displayProgress, setDisplayProgress] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Smooth progress animation between stages
  useEffect(() => {
    setDisplayProgress(targetProgress);
  }, [targetProgress]);

  // Elapsed timer for long-running stages
  useEffect(() => {
    if (!isProcessing) return;
    setElapsedSeconds(0);
    const interval = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [status, isProcessing]);

  // Simulated sub-progress within a stage (so bar appears to move)
  const [subProgress, setSubProgress] = useState(0);
  useEffect(() => {
    if (!isProcessing) return;
    setSubProgress(0);
    const interval = setInterval(() => {
      setSubProgress((p) => {
        // Slowly creep toward next stage but never reach it
        const nextStageIdx = stages.findIndex((s) => s.key === status) + 1;
        const nextWeight = nextStageIdx < stages.length ? stages[nextStageIdx].weight : 100;
        const gap = nextWeight - targetProgress;
        const maxCreep = targetProgress + gap * 0.8; // creep up to 80% of the gap
        const newP = p + (maxCreep - p) * 0.02;
        return Math.min(newP, maxCreep);
      });
    }, 500);
    return () => clearInterval(interval);
  }, [status, targetProgress, isProcessing]);

  const effectiveProgress = isProcessing
    ? Math.max(displayProgress, subProgress)
    : displayProgress;

  return (
    <div className="text-center py-8">
      {/* Progress bar */}
      {status !== 'error' && (
        <div className="mb-6 max-w-sm mx-auto">
          <div className="h-2 rounded-pill bg-dark-subtle overflow-hidden">
            <motion.div
              className="h-full rounded-pill bg-gradient-to-r from-indigo-500 to-indigo-400"
              animate={{ width: `${effectiveProgress}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-content-dim font-body">
              {Math.round(effectiveProgress)}%
            </span>
            {isProcessing && elapsedSeconds > 0 && (
              <span className="text-xs text-content-dim font-body">
                {elapsedSeconds}s elapsed
              </span>
            )}
          </div>
        </div>
      )}

      {/* Animated spinner for active processing */}
      {isProcessing && (
        <div className="flex justify-center mb-4">
          <motion.div
            className="w-8 h-8 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      )}

      {/* Completion — ThumbsUpCog */}
      {status === 'complete' && (
        <motion.img
          src="/ThumbsUpCog.png"
          alt="Professor Cogni celebrating"
          className="w-20 h-20 mx-auto mb-4 object-contain"
          initial={{ scale: 0, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        />
      )}

      {/* Stage label */}
      <AnimatePresence mode="wait">
        <motion.p
          key={status}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.25 }}
          className={`font-body font-medium mb-1 ${
            status === 'error' ? 'text-error' : status === 'complete' ? 'text-success' : 'text-content-secondary'
          }`}
        >
          {label}
        </motion.p>
      </AnimatePresence>

      {/* Time estimate */}
      {estimate && isProcessing && (
        <p className="text-xs text-content-dim font-body mb-2">
          Usually takes {estimate}
        </p>
      )}

      {/* Document title */}
      {doc?.title && status !== 'uploading' && (
        <p className="text-sm text-content-muted font-body">{doc.title}</p>
      )}

      {/* Concept count */}
      {conceptCount > 0 && (
        <p className="text-xs text-content-dim font-body mt-1">
          {conceptCount} concepts identified
        </p>
      )}

      {/* Error message */}
      {status === 'error' && doc?.processingError && (
        <p className="text-sm text-content-secondary font-body mt-3 max-w-md mx-auto">
          {doc.processingError}
        </p>
      )}

      {/* Quality warning */}
      {doc?.qualityScore !== null && doc?.qualityScore !== undefined && doc.qualityScore < 0.5 && status !== 'error' && (
        <p className="text-xs text-warning font-body mt-2">
          We processed your document, but the structure was hard to parse. Your learning experience might be less detailed than usual.
        </p>
      )}

      {/* Fun facts during processing */}
      {isProcessing && <LoadingFacts />}
    </div>
  );
}
